import { useCallback, useRef } from 'react';
import { useIsMobile } from 'hooks/use-size-class';
import { useModalContext } from 'contexts';
import { useReusableComplianceModal } from 'hooks/use-compliance-modal';
import {
  ClickSource,
  ListingCardClickData,
  ADDRESS_CLICK_SOURCE,
  CARD_CLICK_SOURCE,
  COMPLIANCE_OVERLAY_CLICK_SOURCE,
} from 'components/listing-card/listing-card-v2';
import { ADDRESS_MODAL } from 'contexts';
import { locationsToNotUseListingModals } from 'constants/no-listing-modal-locations';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useIsomorphicLayoutEffect } from 'types/react';
import { getLocalizedUrl } from 'utils/locale/get-localized-url';
import { appendHandleFromCookie } from 'utils/my-link-my-lead/get-handle-from-cookie';
import { useLocale } from './use-locale';
import { useTenantId } from './use-tenant-id';

export type ClickEventTracker = (source: ClickSource) => void;

export type UseListingCardClickOptions = {
  /** Optional analytics events to trigger when different click sources are handled */
  onClickEventTracker?: ClickEventTracker;
  /** Override the default behavior of only opening a modal when not on mobile. Useful for testing or specific use cases */
  openInNewTab?: boolean;
};

/**
 * Hook to handle listing card clicks across the application
 *
 * @param options Configuration options for the hook
 * @returns A callback function that handles listing card clicks
 */
export const useListingCardClick = (options: UseListingCardClickOptions = {}) => {
  const { onClickEventTracker, openInNewTab } = options;
  const isMobile = useIsMobile();
  const { openModal } = useModalContext();
  const openComplianceModal = useReusableComplianceModal(openModal);
  const tenantId = useTenantId();
  const { currentLocale } = useLocale(tenantId);

  const openModalRef = useRef(openModal);
  const onClickEventTrackerRef = useRef(onClickEventTracker);

  useIsomorphicLayoutEffect(() => {
    onClickEventTrackerRef.current = onClickEventTracker;
  }, [onClickEventTracker]);

  // Update the ref when openModal changes
  useIsomorphicLayoutEffect(() => {
    openModalRef.current = openModal;
  }, [openModal]);

  const actualOpenInNewTab = openInNewTab ?? isMobile;

  return useCallback((source: ClickSource, data: ListingCardClickData, _event: React.MouseEvent<HTMLElement>) => {
    // Handle compliance overlay clicks
    if (source === COMPLIANCE_OVERLAY_CLICK_SOURCE) {
      onClickEventTrackerRef.current?.(source);
      
      // On mobile or locations that don't use modals, just open compliance modal
      if (actualOpenInNewTab || locationsToNotUseListingModals.includes(data.stateOrProvince)) {
        openComplianceModal(data.complianceRequirement, data.providerId);
        return;
      }
      
      openModalRef.current?.(ADDRESS_MODAL, { 
        addressPath: data.href, 
        listingId: data.listingId && data.listingId > 0 ? data.listingId : undefined, 
      });
      return;
    }

    if (source === ADDRESS_CLICK_SOURCE) {
      onClickEventTrackerRef.current?.(source);
      const localizedHref = getLocalizedUrl({ locale: currentLocale, tenantId, path: data.href });
      const separator = localizedHref.includes('?') ? '&' : '?';
      const listingIdParam = data.listingId > 0 ? `${separator}listingId=${data.listingId}` : '';
      window?.open(appendHandleFromCookie(`${localizedHref}${listingIdParam}`), '_blank');
      return;
    }

    // Handle card clicks
    if (source === CARD_CLICK_SOURCE) {
      onClickEventTrackerRef.current?.(source);

      // On mobile, check if location should not use listing modals
      if (actualOpenInNewTab || locationsToNotUseListingModals.includes(data.stateOrProvince)) {
        const localizedHref = getLocalizedUrl({ locale: currentLocale, tenantId, path: data.href });
        const separator = localizedHref.includes('?') ? '&' : '?';
        const listingIdParam = data.listingId > 0 ? `${separator}listingId=${data.listingId}` : '';
        window?.open(appendHandleFromCookie(`${localizedHref}${listingIdParam}`), '_blank');
        return;
      }

      // Open address modal for other cases
      // Pass listingId to ensure modal shows the same listing as the card
      openModalRef.current?.(ADDRESS_MODAL, { 
        addressPath: data.href, 
        listingId: data.listingId > 0 ? data.listingId : undefined,
      });
    }
  }, [actualOpenInNewTab, openComplianceModal, currentLocale, tenantId]);
};

/**
 * Convenience hook that automatically tracks analytics events using AnalyticsController
 *
 * @param events Object containing GTM event names to track for each click source
 * @param options Additional configuration options
 * @returns A callback function that handles listing card clicks with automatic analytics
 */
export const useListingCardClickWithAnalytics = (
  events: {
    onComplianceOverlayClick?: string;
    onAddressClick?: string;
    onCardClick?: string;
  },
  options: Omit<UseListingCardClickOptions, 'analytics'> = {}
) => {
  const onClickEventTracker: ClickEventTracker = source => {
    AnalyticsController.trackEventConditionally(
      { event: events.onComplianceOverlayClick }
      , source === COMPLIANCE_OVERLAY_CLICK_SOURCE
    );
    AnalyticsController.trackEventConditionally(
      { event: events.onAddressClick },
      source === ADDRESS_CLICK_SOURCE
    );
    AnalyticsController.trackEventConditionally(
      { event: events.onCardClick },
      source === CARD_CLICK_SOURCE
    );
  };
  return useListingCardClick({ ...options, onClickEventTracker });
};
